import { useMemo } from 'react';

import {
  Column,
  FlexAligner,
  IconButton,
  Label,
  NumberField,
  Row,
  Select,
  Surface,
  Typography,
} from '@scaleaq/scaleui';
import { spacing } from '@scaleaq/sui-tokens/responsive';
import { createColumnHelper, flexRender, useTable, type RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/utils';

import { Filter } from './Filter';
import { resolveColumnAccessorFn } from './resolveColumnAccessorFn.ts';
import {
  EllipsisLong,
  StyledCell,
  StyledHeaderCell,
  StyledRow,
  StyledTable,
  StyledTbody,
} from './table.styles.ts';
import type { TableProps } from './table.types.ts';
import { siteControlTableFeatures } from './tableFeatures.ts';

export function Table<T extends RowData>({
  columns = [],
  data,
  enableSorting = true,
  enableFilters = true,
  align = 'left',
  onColumnFiltersChange,
  onPaginationChange,
  onSortingChange,
  rowCount,
  state,
  withPagination = true,
  manualPagination = true,
  tableColumns: tableColumnsInput,
}: TableProps<T>) {
  const { t } = useTranslation(['common']);

  const justifyByAlign = { left: 'flex-start', center: 'center', right: 'flex-end' };
  const headerJustify = justifyByAlign[align];

  const tableColumns = useMemo(() => {
    if (tableColumnsInput) return tableColumnsInput;
    const columnHelper = createColumnHelper<typeof siteControlTableFeatures, T>();
    return columns.map(c => {
      if (c.type === 'accessor') {
        return columnHelper.accessor(resolveColumnAccessorFn(c), {
          id: c.accessorKey,
          header: () => <Typography>{c.header}</Typography>,
          ...(c.cell && { cell: c.cell }), // only set cell if it is not undefined. Important or nothing will show
          meta: c.meta,
        });
      }
      return columnHelper.display({
        id: c.accessorKey,
        header: () => <Typography>{c.header}</Typography>,
        cell: c?.cell,
      });
    });
  }, [tableColumnsInput, columns]);

  const table = useTable({
    features: siteControlTableFeatures,
    data,
    columns: tableColumns,
    onPaginationChange,
    onColumnFiltersChange,
    state,
    manualPagination,
    rowCount,
    onSortingChange,
    enableSorting,
    enableFilters,
  });

  const pageText = t('common:pagination.pageof', {
    current: table.state.pagination.pageIndex + 1,
    total: table.getPageCount(),
  });

  return (
    <Surface style={{ borderRadius: '1.25rem', padding: '1.25rem' }}>
      <Column>
        <StyledTable>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const headerAlign = header.column.columnDef.meta?.headerAlign;
                  const headerDivider = header.column.columnDef.meta?.headerDivider;
                  return (
                    // Group columns span their children, so a category header sits above them.
                    <StyledHeaderCell
                      key={header.id}
                      colSpan={header.colSpan}
                      $divider={headerDivider}
                    >
                      <Row
                        style={{
                          alignItems: 'center',
                          justifyContent: headerAlign ? justifyByAlign[headerAlign] : headerJustify,
                        }}
                        gap='1.25rem'
                      >
                        {header.column.getIsSorted() === 'asc' && <Icon.ArrowUp />}
                        {header.column.getIsSorted() === 'desc' && <Icon.ArrowDown />}
                        {header.isPlaceholder ? null : (
                          <div
                            style={{
                              cursor: header.column.getCanSort() ? 'pointer' : 'default',
                              padding: '0 1rem',
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                            title={
                              header.column.getCanSort()
                                ? header.column.getNextSortingOrder() === 'asc'
                                  ? t('sortAscending')
                                  : header.column.getNextSortingOrder() === 'desc'
                                    ? t('sortDescending')
                                    : t('clearSort')
                                : undefined
                            }
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                        {header.column.getCanFilter() ? <Filter column={header.column} /> : null}
                      </Row>
                    </StyledHeaderCell>
                  );
                })}
              </tr>
            ))}
          </thead>
          <StyledTbody>
            {table.getRowModel().rows.map(row => (
              <StyledRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <StyledCell key={cell.id} $align={align}>
                    <EllipsisLong>
                      <Typography>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Typography>
                    </EllipsisLong>
                  </StyledCell>
                ))}
              </StyledRow>
            ))}
          </StyledTbody>
          <tfoot>
            {table.getFooterGroups().map(footerGroup => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map(header => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.footer, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </tfoot>
        </StyledTable>
        {withPagination && (
          <FlexAligner fullWidth={true} gap='1.25rem' style={{ marginTop: '0.625rem' }}>
            <FlexAligner alignment={'center-left'} fullWidth={false} scaling={2}>
              <IconButton
                size='large'
                Icon={<Icon.ChevronFirst />}
                onClick={() => table.firstPage()}
                disabled={!table.getCanPreviousPage()}
              />
              <IconButton
                size='large'
                Icon={<Icon.ChevronLeft />}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />
              <IconButton
                size='large'
                Icon={<Icon.ChevronRight />}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
              <IconButton
                size='large'
                Icon={<Icon.ChevronLast />}
                onClick={() => table.lastPage()}
                disabled={!table.getCanNextPage()}
              />
              <Typography style={{ textWrap: 'nowrap' }}>{pageText}</Typography>
            </FlexAligner>
            <FlexAligner alignment={'center-right'} gap={spacing.medium} fullWidth={true}>
              <Label size='regular' label={t('common:pagination.goToPage')} />
              <NumberField
                min='1'
                max={table.getPageCount()}
                width='5rem'
                value={table.state.pagination.pageIndex + 1}
                onChange={value => {
                  const page = value ? Number(value) - 1 : 0;
                  table.setPageIndex(page);
                }}
              />
            </FlexAligner>
            <FlexAligner alignment={'center'} gap={spacing.medium} fullWidth={false}>
              <Label size='regular' label={t('common:pagination.show')} />
              <div style={{ width: 'max-content' }}>
                <Select
                  value={{
                    label: `${table.state.pagination.pageSize}`,
                    value: table.state.pagination.pageSize,
                  }}
                  options={[
                    { label: '10', value: 10 },
                    { label: '20', value: 20 },
                    { label: '30', value: 30 },
                    { label: '40', value: 40 },
                    { label: '50', value: 50 },
                  ]}
                  onChange={v => {
                    table.setPageSize(Number(v?.value));
                  }}
                />
              </div>
            </FlexAligner>
          </FlexAligner>
        )}
      </Column>
    </Surface>
  );
}
